const fmts = new Map<string, Intl.DateTimeFormat>();
/**
 * A cached Intl.DateTimeFormat for these options. `toLocaleDateString(undefined, options)` builds a new formatter on
 * every call (~70 µs; ~2 µs this way), and note lists format a date or two per card on every render.
 */
export function dateFmt(options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = JSON.stringify(options);
  let f = fmts.get(key);
  if (!f) fmts.set(key, (f = new Intl.DateTimeFormat(undefined, options)));
  return f;
}

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

/** Evernote-style relative date for note cards. */
export function relativeDate(ms: number, now = Date.now()): string {
  const d = now - ms;
  if (d < MIN) return "Just now";
  if (d < HOUR) return `${Math.floor(d / MIN)} min ago`;
  const date = new Date(ms);
  const today = new Date(now);
  const startToday = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  if (ms >= startToday) return `${Math.floor(d / HOUR)} hr ago`;
  if (ms >= startToday - DAY) return "Yesterday";
  if (ms >= startToday - 6 * DAY) return `${Math.ceil((startToday - ms) / DAY)} days ago`;
  if (date.getFullYear() === today.getFullYear()) return dateFmt({ month: "short", day: "numeric" }).format(date);
  return dateFmt({ year: "numeric", month: "short", day: "numeric" }).format(date);
}

export function fullDate(ms: number): string {
  return dateFmt({ year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(ms);
}

export function shortDate(ms: number): string {
  const d = new Date(ms);
  const now = new Date();
  return dateFmt(d.getFullYear() === now.getFullYear() ? { month: "short", day: "numeric" } : { year: "numeric", month: "short", day: "numeric" }).format(d);
}

/** Group header for a name, as in Evernote's Tags list: the letter (A–Z), the first Hangul syllable (거, 승…), or #. */
export function groupKey(name: string): string {
  const ch = name.trim().charAt(0);
  if (!ch) return "#";
  const code = ch.charCodeAt(0);
  if ((code >= 0xac00 && code <= 0xd7a3) || (code >= 0x3131 && code <= 0x318e)) return ch;
  if (/[a-z]/i.test(ch)) return ch.toUpperCase();
  return "#";
}

/** Evernote sorts Latin names before Hangul ones (and numbers naturally). */
export const COLLATION = ["en", "ko"];
export const collator = new Intl.Collator(COLLATION, { sensitivity: "base", numeric: true });

export function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}
